'use client';

import classNames from 'classnames';
import { ReactNode, useCallback, useMemo } from 'react';
import TrailStandardCard from '@alltrails/cards/components/TrailStandardCard';
import Typography from '@alltrails/core/components/Typography';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Carousel from '@alltrails/denali/components/Carousel';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useTrailCompleted from '@alltrails/lists/hooks/useTrailCompleted';
import { trailFromAlgoliaTrail } from '@alltrails/trails';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import ShowMoreCard from './ShowMoreCard';
import styles from './styles.module.scss';
import TrailCarouselShimmer from './TrailCarouselShimmer';

export type HitTrail = TrailSearchResult & { exploreMoreUrl?: string };

type Props = {
  hits?: HitTrail[];
  isFirstRiverOnPage?: boolean;
  title: ReactNode;
  className?: string;
};

type TrailCardWithActionsProps = {
  hit: TrailSearchResult;
  languageRegionCode: LanguageRegionCode;
  isFavorite: (itemType: string, itemId: number) => boolean;
  handleFavoriteClick: ReturnType<typeof useReduxListItems>['handleFavoriteClick'];
  className?: string;
};

const TrailCardWithActions = ({ hit, languageRegionCode, isFavorite, handleFavoriteClick, className }: TrailCardWithActionsProps) => {
  const trail = trailFromAlgoliaTrail(hit);
  const trailId = hit.ID;
  const { completed, toggleTrailCompleted } = useTrailCompleted(trailId);

  const onFavoriteClick = useCallback(() => {
    handleFavoriteClick('trail', trailId);
  }, [handleFavoriteClick, trailId]);

  const onCompletionBadgeClick = useCallback(() => {
    toggleTrailCompleted();
  }, [toggleTrailCompleted]);

  return (
    <TrailStandardCard
      className={className}
      languageRegionCode={languageRegionCode}
      trail={trail}
      maxPhotos={1}
      isPhotoZoomEnabled
      isFavorite={isFavorite('trail', trailId)}
      onFavoriteClick={onFavoriteClick}
      isCompleted={completed}
      onCompletionBadgeClick={onCompletionBadgeClick}
    />
  );
};

const TrailCarousel = ({ hits = [], title, className }: Props) => {
  const languageRegionCode = useLanguageRegionCode();

  const analyticsInfo = useMemo(() => ({ page: PageStrings.HOMEPAGE }), []);

  const { isFavorite, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(undefined, undefined, analyticsInfo);

  if (hits.length === 0) {
    return <TrailCarouselShimmer className={classNames(styles.riverShimmer, className)} />;
  }

  const renderItem = (hit: HitTrail, index: number) => {
    if (hit.exploreMoreUrl) {
      return (
        <div className={styles.trailCardContainer} key={hit.exploreMoreUrl}>
          <ShowMoreCard url={hit.exploreMoreUrl} cardIndex={index} />
        </div>
      );
    }

    return (
      <div className={styles.trailCardContainer} key={hit.ID || hit.exploreMoreUrl}>
        <TrailCardWithActions
          hit={hit}
          languageRegionCode={languageRegionCode}
          isFavorite={isFavorite}
          handleFavoriteClick={handleFavoriteClick}
          className={styles.trailCard}
        />
      </div>
    );
  };

  const getItemKey = (hit: HitTrail) => hit.ID?.toString() || hit.exploreMoreUrl || '';

  return (
    <>
      {listModal}
      {signUpModal}
      <div className={classNames(styles.container, className)}>
        <div className={styles.riverContainer}>
          <Typography variant="heading400" component="h2" className={styles.title}>
            {title}
          </Typography>
          <Carousel
            items={hits}
            renderItem={renderItem}
            getItemKey={getItemKey}
            className={styles.carousel}
            leftArrowClassName={styles.leftArrow}
            rightArrowClassName={styles.rightArrow}
            absoluteSizing={true}
            slideAsSet
            showPaginationDots={false}
            showOverflow={false}
          />
        </div>
      </div>
    </>
  );
};

export default TrailCarousel;
